import AddToCartButton from '@/components/AddToCartButton/AddToCartButton'
import { DistributorOnly } from '@/components/Helpers/DistributorOnly'
import T from '@/components/Helpers/Translate'
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from '@/components/_ui/Carousel'
import { Skeleton } from '@/components/_ui/Skeleton'
import type { CosmicProductType } from '@/lib/cms/cosmic/types/CosmicProductType'
import { useGTM } from '@/lib/hooks/google-analytics/useGTM'
import { usePriceHandler } from '@/lib/hooks/helpers/usePriceHandler'
import useParseLinkUrlWithLocale from '@/lib/hooks/routing/useParseLinkUrlWithLocale'
import { useGetProductRecommendations } from '@/lib/hooks/services/useGetProductRecommendations'
import { getTerms } from '@/lib/hooks/services/useGetTerms'
import { generateAddToCartEventData } from '@/lib/services/mixpanel/helpers/generateCartEventData'
import { mixpanelService } from '@/lib/services/mixpanel/initializeMixPanel'
import { MixpanelEvent } from '@/lib/services/mixpanel/mixpanelEvent'
import { getProductRecommendationSlugArray } from '@/lib/utils/shared/getProductRecommendationSlugArray'
import type { CartItemType } from '@/store/types/cart'
import useGlobalStore from '@/store/useGlobalStore'
import Image from 'next/image'

type Props = {
  productData: CosmicProductType
}

const FrequentlyBoughtTogether = ({ productData }: Props) => {
  const {
    user,
    addToCart,
    setApp,
    app: { marketExceptions },
  } = useGlobalStore()
  const { priceHandler } = usePriceHandler()
  const { gtm } = useGTM()
  const { parseLinkUrlWithLocale } = useParseLinkUrlWithLocale()
  const { isLoading, recommendedProducts } = useGetProductRecommendations({
    recommendedProductsStringArray:
      getProductRecommendationSlugArray({
        productData,
      }) || [],
  })

  const isAssociate = user.customerType === 'Associate'
  const isMember =
    (user.customerType !== 'Customer' && user.refName !== null) ||
    user.customerType === 'Member'

  const handleAddToCart = ({ item }: { item: CosmicProductType }) => {
    const itemToAddToCart: CartItemType = {
      subscriptionDiscount: productData.subscriptionDiscount || 0,
      slug: item.slug,
      title: item.name,
      image:
        item.variations[0].media?.imageUrls?.[0].url || item.imageUrl || '',
      description: item.description,
      po: 'otp',
      purchaseOptions: productData.purchaseOptions || null,
      qty: 1,
      product: item.variations[0],
      recommendedProducts: getProductRecommendationSlugArray({ productData }),
      guarantee: productData.policies?.[0] || null,
      addToCartLocation: ['product_page_frequently_bougth'],
      categories: item.categories,
    }

    gtm.addToCart({
      cartItem: itemToAddToCart,
      event_location: 'product_page_frequenlty_bougth',
    })

    mixpanelService
      .trackEvent(MixpanelEvent.ADD_TO_CART, {
        event_location: 'product_page_frequently_bougth',
        cart: generateAddToCartEventData({
          cartItem: itemToAddToCart,
          currency: marketExceptions?.currencyCode || 'USD',
          price:
            getTerms({
              forCustomerType: user.customerType,
              pricingData: item.variations[0].pricing,
            })?.priceEach || 0,
          quantity: 1,
          addToCartLocation: 'product_page_frequently_bought',
          focus: false,
        }),
        item_count: 1,
      })
      .catch(() => {})

    addToCart(itemToAddToCart)
    setTimeout(() => setApp({ isCartOpen: true }), 100)
  }

  if (isLoading) {
    return (
      <div>
        <div className="font-primary mt-9 mb-5 font-semibold">
          <T>frequently_bought_together</T>
        </div>
        <div className="hidden space-y-3 md:block">
          <Skeleton className="h-28" />
          <Skeleton className="h-28" />
          <Skeleton className="h-28" />
        </div>
        <div className="space flex justify-between space-x-3 md:hidden">
          <Skeleton className="h-36 w-1/2" />
          <Skeleton className="h-36 w-1/2" />
        </div>
      </div>
    )
  }

  if (!recommendedProducts) return null

  return (
    <div data-test="frequenty-bought-together">
      <div className="font-primary mt-9 mb-5 font-semibold">
        <T>frequently_bought_together</T>
      </div>
      <div className="hidden rounded-2xl border px-6 py-2 md:block">
        {recommendedProducts.map((item, index) => {
          const terms = getTerms({
            forCustomerType: user.customerType,
            pricingData: item.variations[0].pricing,
          })

          return (
            <div
              className={`flex flex-col items-center justify-between lg:flex-row ${
                index < recommendedProducts.length - 1 && 'border-b'
              }`}
              key={`frequently-bought-${item.slug}`}
            >
              <a
                className="no-underline"
                href={parseLinkUrlWithLocale({
                  url: `/product/${item.slug}`,
                })}
              >
                <div
                  className="flex flex-row"
                  data-test={`frequenty-bought-together-item-${index}`}
                >
                  <Image
                    src={item.imageUrl || ''}
                    alt={item.image?.altText || item.name}
                    className="mr-4 max-w-[100px] cursor-pointer"
                    height={80}
                    width={100}
                  />
                  <div className="flex flex-col justify-center pl-2">
                    <span className="cursor-pointer text-lg font-semibold text-primary">
                      {item.name}
                    </span>
                    <div className="flex flex-col">
                      <div className="flex flex-col xl:flex-row xl:space-x-3">
                        <DistributorOnly>
                          <span>
                            <T>PV</T> {terms?.pvEach}
                          </span>
                          <span className="hidden xl:ml-3 xl:inline">|</span>
                        </DistributorOnly>

                        {isAssociate || isMember ? (
                          <span className="text-[#398485]">
                            <T>{isAssociate ? 'associate' : 'member'}</T>{' '}
                            {priceHandler({
                              forCustomerType: user.customerType,
                              pricingData: item.variations[0].pricing,
                              currency: marketExceptions?.currencyCode,
                            })}
                          </span>
                        ) : (
                          <span>
                            <T>price</T>{' '}
                            {priceHandler({
                              forCustomerType: user.customerType,
                              pricingData: item.variations[0].pricing,
                              currency: marketExceptions?.currencyCode,
                            })}
                          </span>
                        )}
                      </div>

                      {marketExceptions?.tax?.inclusive && (
                        <div>
                          <T>vat_included</T>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </a>
              <AddToCartButton
                onClick={() => {
                  handleAddToCart({ item })
                }}
                className="mb-0 max-h-9.5 max-w-full p-1 text-sm md:mt-0 md:mb-5 lg:mt-5 lg:max-w-[140px]"
                data-test="frequenty-bought-together-add-to-cart"
              >
                <T>add_to_cart</T>
              </AddToCartButton>
            </div>
          )
        })}
      </div>
      <div className="md:hidden">
        <Carousel
          opts={{
            align: 'start',
          }}
        >
          <CarouselContent>
            {recommendedProducts.map((item, index) => {
              const terms = getTerms({
                forCustomerType: user.customerType,
                pricingData: item.variations[0].pricing,
              })

              return (
                <CarouselItem
                  key={`frequently-bought-${item.image?.imageUrl}`}
                  className="max-w-52"
                >
                  <div className="flex flex-col items-center rounded-xl border border-[#15386280] p-3">
                    <a
                      className="flex w-full flex-col items-center no-underline"
                      href={parseLinkUrlWithLocale({
                        url: `/product/${item.slug}`,
                      })}
                    >
                      <Image
                        className="cursor-pointer"
                        data-test={`frequenty-bought-together-item-${index}`}
                        src={item.image?.imageUrl || item.imageUrl || ''}
                        alt={item.image?.altText || item.name}
                        height={80}
                        width={100}
                      />

                      <div className="w-full text-primary">
                        <span className="cursor-pointer text-sm font-semibold">
                          {item.name}
                        </span>
                        <div className="flex flex-col text-xs">
                          <DistributorOnly>
                            <span>
                              <T>PV</T> {terms?.pvEach}
                            </span>
                          </DistributorOnly>

                          {isAssociate || isMember ? (
                            <span className="text-[#398485]">
                              <T>{isAssociate ? 'associate' : 'member'}</T>{' '}
                              {priceHandler({
                                forCustomerType: user.customerType,
                                pricingData: item.variations[0].pricing,
                                currency: marketExceptions?.currencyCode,
                              })}
                            </span>
                          ) : (
                            <span>
                              <T>price</T>{' '}
                              {priceHandler({
                                forCustomerType: user.customerType,
                                pricingData: item.variations[0].pricing,
                                currency: marketExceptions?.currencyCode,
                              })}
                            </span>
                          )}
                          {marketExceptions?.tax?.inclusive && (
                            <span>
                              <T>vat_included</T>
                            </span>
                          )}
                        </div>
                      </div>
                    </a>
                    <AddToCartButton
                      onClick={() => {
                        handleAddToCart({ item })
                      }}
                      className="h-7 text-sm"
                      data-test="frequenty-bought-together-add-to-cart"
                    >
                      <T>add_to_cart</T>
                    </AddToCartButton>
                  </div>
                </CarouselItem>
              )
            })}
          </CarouselContent>
        </Carousel>
      </div>
    </div>
  )
}

export default FrequentlyBoughtTogether
